import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { TablePaginationConfig } from 'antd/es/table';
import { ColumnProps } from 'antd/lib/table';
import { SorterResult, SortOrder } from 'antd/lib/table/interface';
import dayjs from 'dayjs';

import DeviceSoftwareEndOfSupportContext from '@totem/components/devices/softwareEndOfLife/summary/DeviceSoftwareEndOfSupportContext';
import { DeviceSoftwareEndOfSupportRecord } from '@totem/components/devices/softwareEndOfLife/summary/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import InputContainer from '@totem/components/ui_components/reportSettings/InputContainer';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import { isNotNull } from '@totem/utilities/common';
import { sortStringAscending } from '@totem/utilities/tableUtilities';

const localizedFormat = require('dayjs/plugin/localizedFormat');
dayjs.extend(localizedFormat);

const PastEndOfLifeReportTable = () => {
  const { loading, data } = useContext(DeviceSoftwareEndOfSupportContext);
  const { input, handleTableChange } = useContext(InputContext);

  const getSortOrder = (key: string): SortOrder =>
    input.sort.field === key ? (input.sort.direction as SortOrder) : null;

  const columns: ColumnProps<DeviceSoftwareEndOfSupportRecord>[] = [
    {
      title: 'Device Name',
      dataIndex: 'displayName',
      key: 'displayName',
      render: (_, record: DeviceSoftwareEndOfSupportRecord) => {
        return (
          <Link to={`/dashboard/devices/${record.deviceId}`}>
            {record.displayName}
          </Link>
        );
      },
      sortDirections: ['ascend', 'descend'],
      sortOrder: getSortOrder('displayName'),
      sorter: (compA, compB) =>
        sortStringAscending(compA.displayName, compB.displayName),
    },
    {
      title: 'Region',
      dataIndex: 'regionName',
      key: 'regionName',
      render: (_, record: DeviceSoftwareEndOfSupportRecord) => {
        return <span>{record.regionName}</span>;
      },
      sortDirections: ['ascend', 'descend'],
      sortOrder: getSortOrder('regionName'),
      sorter: (compA, compB) =>
        sortStringAscending(compA.regionName, compB.regionName),
    },
    {
      title: 'Building',
      dataIndex: 'buildingName',
      key: 'buildingName',
      render: (_, record: DeviceSoftwareEndOfSupportRecord) => {
        return (
          <Link to={`/dashboard/buildings/${record.buildingId}`}>
            {record.buildingName}
          </Link>
        );
      },
      sortDirections: ['ascend', 'descend'],
      sortOrder: getSortOrder('buildingName'),
      sorter: (compA, compB) =>
        sortStringAscending(compA.buildingName, compB.buildingName),
    },
    {
      title: 'Control System',
      dataIndex: 'controlSystemName',
      key: 'controlSystemName',
      render: (_, record: DeviceSoftwareEndOfSupportRecord) => {
        return (
          <Link to={`/dashboard/controlsystems/${record.controlSystemId}`}>
            {record.controlSystemName}
          </Link>
        );
      },
      sortDirections: ['ascend', 'descend'],
      sortOrder: getSortOrder('controlSystemName'),
      sorter: (compA, compB) =>
        sortStringAscending(compA.controlSystemName, compB.controlSystemName),
    },
    {
      title: 'Software Package',
      key: 'softwarePackageName',
      dataIndex: 'softwarePackageName',
      render: (_, record: DeviceSoftwareEndOfSupportRecord) => {
        return (
          <Link to={`/dashboard/software/${record.softwarePackageId}`}>
            {record.softwarePackageName}
          </Link>
        );
      },
      sortDirections: ['ascend', 'descend'],
      sortOrder: getSortOrder('softwarePackageName'),
      sorter: (compA, compB) =>
        sortStringAscending(
          compA.softwarePackageName,
          compB.softwarePackageName,
        ),
    },
    {
      title: 'Version',
      dataIndex: 'softwareVersion',
      key: 'softwareVersion',
      render: (_, record: DeviceSoftwareEndOfSupportRecord) => {
        return record.softwareVersion;
      },
      sortDirections: ['ascend', 'descend'],
      sortOrder: getSortOrder('softwareVersion'),
      sorter: (compA, compB) =>
        sortStringAscending(compA.softwareVersion, compB.softwareVersion),
    },
    {
      title: 'Publisher',
      dataIndex: 'softwarePublisher',
      key: 'softwarePublisher',
      render: (_, record: DeviceSoftwareEndOfSupportRecord) => {
        return record.softwarePublisher;
      },
      sortDirections: ['ascend', 'descend'],
      sortOrder: getSortOrder('softwarePublisher'),
      sorter: (compA, compB) =>
        sortStringAscending(compA.softwarePublisher, compB.softwarePublisher),
    },
    {
      title: 'End-of-Support Date',
      dataIndex: 'softwareEndOfSupportDate',
      key: 'softwareEndOfSupportDate',
      render: (_, record: DeviceSoftwareEndOfSupportRecord) => {
        return record.softwareEndOfSupportDate === '0001-01-01T00:00:00Z'
          ? 'Date Not Available'
          : dayjs(record.softwareEndOfSupportDate).format('L');
      },
      sortDirections: ['ascend', 'descend'],
      sortOrder: getSortOrder('softwareEndOfSupportDate'),
      sorter: (compA, compB) =>
        sortStringAscending(
          compA.softwareEndOfSupportDate,
          compB.softwareEndOfSupportDate,
        ),
    },
  ];

  const getData = () => {
    if (isNotNull(data) && isNotNull(data.pastEndOfSupport)) {
      return data.pastEndOfSupport;
    }
    return [];
  };

  return (
    <IBTable
      showSorterTooltip
      columns={columns}
      dataSource={getData()}
      loading={loading}
      onChange={(
        updatedPagination: TablePaginationConfig,
        filters,
        sorter: SorterResult<DeviceSoftwareEndOfSupportRecord>,
        extra,
      ) => handleTableChange(updatedPagination, filters, sorter, extra)}
      pagination={{
        current: input.pagination.page,
        pageSize: input.pagination.pageSize,
        total: getData().length,
        showSizeChanger: true,
      }}
      rowKey={(record) =>
        record.deviceId + record.softwarePackageId + record.softwareVersion
      }
    />
  );
};

type Props = {
  componentKey?: string;
};

const PastEndOfLifeReport = ({ componentKey }: Props) => (
  <InputContainer
    filterConfig={[]}
    defaultSort={{ field: 'displayName', direction: 'ascend' }}
    componentKey={componentKey}
  >
    <PastEndOfLifeReportTable />
  </InputContainer>
);

export default PastEndOfLifeReport;
